import React, { useState, useEffect } from 'react';
import { Box, Container, Card, TextField, Button, CircularProgress, Typography, Alert, Snackbar, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { campaignService, campaignCategoryService, extractApiDataList } from '../services/api';
import { ArrowBack, CloudUpload, Save } from '@mui/icons-material';

const CampaignAddPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    campaignCategoryService
      .getAll({ include_inactive: 0 })
      .then((res) => setCategories(extractApiDataList(res)))
      .catch(() => setCategories([]));
  }, []);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    content: '',
    target_amount: '',
    current_amount: '0',
    start_date: '',
    end_date: '',
    status: 'active',
    donation_variant: 'default',
    category_id: '',
    image_url: '',
    video_url: '',
    map_embed_url: '',
    map_lat: '',
    map_lng: '',
    map_address: ''
  });

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerateSlug = () => {
    if (formData.title) {
      const slug = formData.title
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.title || !formData.slug || !formData.description) {
        setToast({
          open: true,
          message: 'Please fill in all required fields (Title, Slug, Description)',
          type: 'error'
        });
        return;
      }

      setLoading(true);

      if (selectedImage) {
        const formDataToSend = new FormData();
        formDataToSend.append('image', selectedImage);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('slug', formData.slug);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('content', formData.content);
        formDataToSend.append('target_amount', formData.target_amount);
        formDataToSend.append('current_amount', formData.current_amount);
        formDataToSend.append('start_date', formData.start_date);
        formDataToSend.append('end_date', formData.end_date);
        formDataToSend.append('status', formData.status);
        formDataToSend.append('video_url', formData.video_url);
        formDataToSend.append('map_embed_url', formData.map_embed_url || '');
        formDataToSend.append('map_lat', formData.map_lat || '');
        formDataToSend.append('map_lng', formData.map_lng || '');
        formDataToSend.append('map_address', formData.map_address || '');
        formDataToSend.append('donation_variant', formData.donation_variant || 'default');
        if (formData.category_id) formDataToSend.append('category_id', String(formData.category_id));

        await campaignService.createCampaignWithFile(formDataToSend);
      } else {
        const payload = { ...formData };
        if (!payload.category_id) delete payload.category_id;
        await campaignService.createCampaign(payload);
      }

      setToast({
        open: true,
        message: 'Campaign created successfully!',
        type: 'success'
      });

      setTimeout(() => {
        navigate('/admin/campaigns');
      }, 1500);
    } catch (error) {
      console.error('Error saving campaign:', error);
      setToast({
        open: true,
        message: 'Error creating campaign: ' + (error.response?.data?.error || error.message),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/campaigns')}
            sx={{ color: '#2c5f2d' }}
          >
            Back to Campaigns
          </Button>
        </Box>

        <Card sx={{ 
          border: '1px solid #e8ecf1',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          p: 3
        }}>
          <Typography variant="h5" sx={{ fontWeight: '600', color: '#1a202c', mb: 3 }}>
            Add New Campaign
          </Typography>

          <Grid container spacing={3}>
            {/* Image Upload Section */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f8fafb', borderRadius: '8px', border: '1px solid #e8ecf1' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                  Campaign Image
                </Typography>
                
                {imagePreview && (
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <img 
                      src={imagePreview} 
                      alt="Campaign preview" 
                      style={{
                        maxWidth: '100%',
                        maxHeight: '250px',
                        borderRadius: '8px',
                        border: '2px solid #e8ecf1'
                      }}
                    />
                  </Box>
                )}

                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUpload />}
                  sx={{
                    borderColor: '#2c5f2d',
                    color: '#2c5f2d',
                    py: 2,
                    '&:hover': {
                      backgroundColor: '#f0f7ff',
                      borderColor: '#1f4620',
                    }
                  }}
                >
                  Upload Campaign Image
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleImageUpload}
                  />
                </Button>
                <Typography variant="caption" sx={{ color: '#667085', mt: 1, display: 'block' }}>
                  Recommended: 400x250 pixels (JPG or PNG, max 5MB)
                </Typography>
              </Box>
            </Grid>

            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Campaign Title *"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="e.g., Clean Water Initiative"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      label="Slug *"
                      name="slug"
                      value={formData.slug}
                      onChange={handleFormChange}
                      placeholder="e.g., clean-water-initiative"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleGenerateSlug}
                      sx={{ mt: 1, borderColor: '#2c5f2d', color: '#2c5f2d' }}
                    >
                      Generate
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description *"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    multiline
                    rows={3}
                    placeholder="Brief description of the campaign"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Detailed Content"
                    name="content"
                    value={formData.content}
                    onChange={handleFormChange}
                    multiline
                    rows={4}
                    placeholder="Detailed information about the campaign"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Financial Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                Financial Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Target Amount"
                    name="target_amount"
                    type="number"
                    value={formData.target_amount}
                    onChange={handleFormChange}
                    placeholder="0"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Current Amount"
                    name="current_amount"
                    type="number"
                    value={formData.current_amount}
                    onChange={handleFormChange}
                    placeholder="0"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Timeline */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                Campaign Timeline
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    name="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    name="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleFormChange}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                    <option value="draft">Draft</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Category"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleFormChange}
                    SelectProps={{ native: true }}
                    helperText="Manage categories under Campaign Categories in the menu."
                  >
                    <option value="">— None —</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Donation form type"
                    name="donation_variant"
                    value={formData.donation_variant}
                    onChange={handleFormChange}
                    SelectProps={{ native: true }}
                    helperText="After save, edit the campaign to add sponsor tiers for School kit."
                  >
                    <option value="default">Standard (default)</option>
                    <option value="school_kit">School kit distribution</option>
                    <option value="freedom_hunger">Freedom from hunger</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Video URL"
                    name="video_url"
                    value={formData.video_url}
                    onChange={handleFormChange}
                    placeholder="https://youtube.com/..."
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Map */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                Map (optional)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Map embed URL" name="map_embed_url" value={formData.map_embed_url} onChange={handleFormChange} placeholder="https://www.google.com/maps/embed?pb=..." />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Latitude" name="map_lat" value={formData.map_lat} onChange={handleFormChange} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Longitude" name="map_lng" value={formData.map_lng} onChange={handleFormChange} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Address / label" name="map_address" value={formData.map_address} onChange={handleFormChange} />
                </Grid>
              </Grid>
            </Grid>

            {/* Actions */}
            <Grid item xs={12} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/campaigns')}
                sx={{ borderColor: '#e8ecf1', color: '#667085' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={loading}
                sx={{ 
                  backgroundColor: '#2c5f2d',
                  '&:hover': { backgroundColor: '#1f4620' }
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Campaign'}
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Container>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.type}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignAddPage;
